import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { ComplaintCard } from "./complaints/ComplaintCard";
import { NewComplaintDialog, COMPLAINT_CATEGORIES } from "./complaints/NewComplaintDialog";

const countLabel = (count, isAdmin) => {
  if (count === 0) return "No complaints raised.";
  const plural = count > 1 ? "s" : "";
  const scope = isAdmin ? " (all customers)" : "";
  return `${count} complaint${plural}${scope}.`;
};

export const ComplaintsPanel = ({ bookings, user }) => {
  const isAdmin = user?.role === "admin";
  const [complaints, setComplaints] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ booking_ref: "", category: COMPLAINT_CATEGORIES[0], subject: "", description: "" });
  const [reply, setReply] = useState({ message: "", status: "in_review" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/complaints").then((r) => setComplaints(r.data)).catch(() => setComplaints([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const availableBookings = bookings || [];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/complaints", form);
      toast.success(`Complaint ${data.complaint_id} submitted — we'll respond within 2 business days`);
      setOpen(false);
      setForm({ booking_ref: "", category: COMPLAINT_CATEGORIES[0], subject: "", description: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const respond = async (complaintId) => {
    setBusy(true);
    try {
      await api.post(`/complaints/${complaintId}/respond`, reply);
      toast.success("Response sent");
      setReply({ message: "", status: "in_review" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (complaints === null)
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>;

  return (
    <div data-testid="complaints-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <p className="text-sm text-[#57685e]">{countLabel(complaints.length, isAdmin)}</p>
        {!isAdmin && availableBookings.length > 0 && (
          <button onClick={() => { setForm((f) => ({ ...f, booking_ref: availableBookings[0].booking_ref })); setOpen(true); }}
            className="btn-cta rounded px-5 py-2.5 text-xs flex items-center gap-2" data-testid="new-complaint-btn">
            <Plus className="w-4 h-4" /> New complaint
          </button>
        )}
      </div>

      <div className="space-y-4">
        {complaints.map((c) => (
          <ComplaintCard key={c.complaint_id} c={c} isAdmin={isAdmin} isOpen={expanded === c.complaint_id}
            onToggle={() => setExpanded(expanded === c.complaint_id ? null : c.complaint_id)}
            reply={reply} setReply={setReply} busy={busy} onRespond={respond} />
        ))}
      </div>

      <NewComplaintDialog open={open} setOpen={setOpen} form={form} setForm={setForm}
        bookings={availableBookings} busy={busy} onSubmit={submit} />
    </div>
  );
};
